import {
  DataProps,
  QIcon,
  QSpinner,
  QStack,
  QStackProps,
  QText,
} from '@qualio/ui-components';
import { type PendingAction } from 'ckeditor5';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { EditorStatus, EditorStatusContext } from '../../Context';
import { QMultiRootEditorContext } from '../../QMultiRootEditor/Context';

const AutosaveStack = ({ children, ...props }: QStackProps) => {
  return (
    <QStack
      direction="row"
      alignItems="center"
      data-cy="autosave-notification"
      {...props}
    >
      {children}
    </QStack>
  );
};

const AutosaveLabel = ({
  label,
  ...dataProps
}: { label: string } & DataProps) => {
  return (
    <QText color="gray.500" fontSize="xs" whiteSpace="nowrap" {...dataProps}>
      {label}
    </QText>
  );
};

export const AutoSaveNotification = () => {
  const { t } = useTranslation();
  const { docEditorStatus, ccEditorStatus, trainingAssessmentStatus } =
    useContext(EditorStatusContext);

  const { setDisabled } = useContext(ButtonGroupContext);

  const { editor: docEditor, ccEditor } = useContext(QMultiRootEditorContext);

  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  useEffect(() => {
    const editors = [docEditor, ccEditor].filter(Boolean);
    if (editors.length === 0) return;

    let actionMessageCleanups: Array<() => void> = [];

    const syncMessage = () => {
      const docPending = docEditor?.plugins.get('PendingActions');
      const ccPending = ccEditor?.plugins.get('PendingActions');
      if (docPending?.hasAny) {
        setPendingMessage(docPending.first?.message ?? null);
      } else if (ccPending?.hasAny) {
        setPendingMessage(ccPending.first?.message ?? null);
      } else {
        setPendingMessage(null);
      }
    };

    const registerActionMessageListener = (action: PendingAction) => {
      action.on('change:message', syncMessage);
      actionMessageCleanups.push(() =>
        action.off('change:message', syncMessage),
      );
    };

    const syncActionMessageListeners = () => {
      actionMessageCleanups.forEach((cleanup) => cleanup());
      actionMessageCleanups = [];

      editors.forEach((editor) => {
        const pendingActions = editor!.plugins.get('PendingActions');
        Array.from(pendingActions).forEach(registerActionMessageListener);
      });
    };

    const handlePendingActionsChange = () => {
      syncActionMessageListeners();
      syncMessage();
    };

    const cleanups = editors.map((editor) => {
      const pendingActions = editor!.plugins.get('PendingActions');
      pendingActions.on('change:hasAny', handlePendingActionsChange);
      pendingActions.on('add', handlePendingActionsChange);
      pendingActions.on('remove', handlePendingActionsChange);
      return () => {
        pendingActions.off('change:hasAny', handlePendingActionsChange);
        pendingActions.off('add', handlePendingActionsChange);
        pendingActions.off('remove', handlePendingActionsChange);
      };
    });

    syncActionMessageListeners();
    syncMessage();

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      actionMessageCleanups.forEach((cleanup) => cleanup());
    };
  }, [docEditor, ccEditor]);

  const isConnecting =
    docEditorStatus === EditorStatus.CONNECTING ||
    ccEditorStatus === EditorStatus.CONNECTING;

  const isSaving =
    docEditorStatus === EditorStatus.SAVING ||
    ccEditorStatus === EditorStatus.SAVING ||
    trainingAssessmentStatus === EditorStatus.SAVING;

  const isPendingSave =
    docEditorStatus === EditorStatus.PENDING_AUTOSAVE ||
    ccEditorStatus === EditorStatus.PENDING_AUTOSAVE;

  const hasError =
    docEditorStatus === EditorStatus.ERROR ||
    ccEditorStatus === EditorStatus.ERROR;

  useEffect(() => {
    setDisabled(isSaving || isPendingSave || hasError);
  }, [isSaving, isPendingSave, hasError, setDisabled]);

  if (isConnecting) {
    return (
      <AutosaveStack>
        <QSpinner data-cy="autosave-spinner" size="sm" />
      </AutosaveStack>
    );
  }

  //handling saving states

  const hasFinishedSaving =
    docEditorStatus === EditorStatus.SAVED ||
    ccEditorStatus === EditorStatus.SAVED ||
    trainingAssessmentStatus === EditorStatus.SAVED;

  if (docEditorStatus === EditorStatus.ERROR) {
    return (
      <AutosaveStack>
        <AutosaveLabel
          data-cy="autosave-document-error-label"
          label={t('errors.errorSavingRecentDocumentEdit')}
        />
      </AutosaveStack>
    );
  } else if (ccEditorStatus === EditorStatus.ERROR) {
    return (
      <AutosaveStack>
        <AutosaveLabel
          data-cy="autosave-cc-error-label"
          label={t('errors.errorSavingRecentChangeControlEdit')}
        />
      </AutosaveStack>
    );
  }

  if (pendingMessage) {
    return (
      <AutosaveStack>
        <AutosaveLabel
          data-cy="autosave-pending-label"
          label={pendingMessage}
        />
      </AutosaveStack>
    );
  }

  if (isSaving || isPendingSave) {
    return (
      <AutosaveStack>
        <AutosaveLabel
          data-cy="autosave-saving-label"
          label={t('labels.savingChanges')}
        />
      </AutosaveStack>
    );
  } else if (hasFinishedSaving) {
    return (
      <AutosaveStack gap="4px">
        <QIcon iconName="Check" size="sm" color="green.500" />
        <AutosaveLabel
          data-cy="autosave-changes-saved-label"
          label={t('labels.changesSaved')}
        />
      </AutosaveStack>
    );
  }

  return null;
};
